import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  UserCheck, CalendarDays, LayoutGrid, Loader2, AlertCircle, Save, CheckCircle2,
} from 'lucide-react';
import apiClient from '../../config/api.config';

type Statut = 'present' | 'absent' | 'remplace' | 'excuse' | '';

const STATUTS: { v: Exclude<Statut, ''>; label: string; court: string; bg: string; fg: string; dot: string }[] = [
  { v: 'present', label: 'Présent', court: 'P', bg: 'bg-green-100 dark:bg-green-900/40', fg: 'text-green-700 dark:text-green-300', dot: '#16a34a' },
  { v: 'absent', label: 'Absent', court: 'A', bg: 'bg-red-100 dark:bg-red-900/40', fg: 'text-red-700 dark:text-red-300', dot: '#dc2626' },
  { v: 'remplace', label: 'Remplacé', court: 'R', bg: 'bg-amber-100 dark:bg-amber-900/40', fg: 'text-amber-700 dark:text-amber-300', dot: '#d97706' },
  { v: 'excuse', label: 'Justifié', court: 'J', bg: 'bg-sky-100 dark:bg-sky-900/40', fg: 'text-sky-700 dark:text-sky-300', dot: '#0284c7' },
];
const STAT_MAP = Object.fromEntries(STATUTS.map((s) => [s.v, s]));

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDays = (iso: string, n: number) => {
  const d = new Date(iso + 'T00:00:00'); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10);
};
const jourLabel = (iso: string) =>
  new Date(iso + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' });

interface Creneau {
  edt_id: number; classe: string; libelle_matiere: string; formateur_nom: string;
  formateur_id: number | null; creneau: string; heure_debut: string; pole_nom: string;
  statut: Statut; motif: string; remplacant_nom: string; saisi: boolean; saisi_par_nom: string;
}

const PresenceFormateurs: React.FC = () => {
  const [tab, setTab] = useState<'saisie' | 'dashboard'>('saisie');
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <UserCheck size={26} /> Présence des formateurs
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Les coordonnateurs notent chaque soir si le formateur a fait cours ; le DAAC suit la présence sur le tableau de bord.
        </p>
      </div>
      <div className="flex gap-2 mb-5 border-b border-gray-200 dark:border-gray-700">
        <button onClick={() => setTab('saisie')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'saisie' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          <CalendarDays size={16} /> Saisie du jour
        </button>
        <button onClick={() => setTab('dashboard')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'dashboard' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          <LayoutGrid size={16} /> Tableau de bord (DAAC)
        </button>
      </div>
      {tab === 'saisie' ? <Saisie /> : <Dashboard />}
    </div>
  );
};

// ───────────────────────── Saisie (coordonnateur) ─────────────────────────
const Saisie: React.FC = () => {
  const [date, setDate] = useState(todayISO());
  const [pole, setPole] = useState('');
  const [poles, setPoles] = useState<string[]>([]);
  const [rows, setRows] = useState<Creneau[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ t: 'ok' | 'err' | 'info'; m: string } | null>(null);

  const charger = useCallback(async () => {
    setLoading(true); setMsg(null);
    try {
      const r = await apiClient.get('/vie-scolaire/presence-formateurs/jour/', {
        params: { date, ...(pole ? { pole } : {}) },
      });
      setRows(r.data.items || []);
      setPoles(r.data.poles || []);
      if ((r.data.items || []).length === 0) setMsg({ t: 'info', m: 'Aucun cours prévu à l’emploi du temps pour cette date.' });
    } catch (e: any) {
      setMsg({ t: 'err', m: e?.response?.data?.detail || 'Chargement impossible.' });
    } finally { setLoading(false); }
  }, [date, pole]);

  useEffect(() => { charger(); /* eslint-disable-next-line */ }, [date]);

  const setStatut = (i: number, s: Statut) =>
    setRows((prev) => prev.map((r, k) => (k === i ? { ...r, statut: r.statut === s ? '' : s } : r)));
  const setChamp = (i: number, champ: keyof Creneau, val: string) =>
    setRows((prev) => prev.map((r, k) => (k === i ? { ...r, [champ]: val } : r)));
  const toutPresent = () => setRows((prev) => prev.map((r) => ({ ...r, statut: 'present' })));

  const enregistrer = async () => {
    const items = rows.filter((r) => r.statut);
    if (items.length === 0) { setMsg({ t: 'info', m: 'Notez au moins une présence avant d’enregistrer.' }); return; }
    setSaving(true); setMsg(null);
    try {
      const r = await apiClient.post('/vie-scolaire/presence-formateurs/', { date, items });
      setMsg({ t: 'ok', m: `${r.data.enregistres} présence(s) enregistrée(s). Le DAAC peut les consulter.` });
      charger();
    } catch (e: any) {
      setMsg({ t: 'err', m: e?.response?.data?.detail || 'Enregistrement impossible.' });
    } finally { setSaving(false); }
  };

  const nbNotes = rows.filter((r) => r.statut).length;

  return (
    <div>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Date</label>
            <input type="date" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
          </div>
          {poles.length > 1 && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Pôle</label>
              <select value={pole} onChange={(e) => { setPole(e.target.value); }}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                <option value="">Tous les pôles</option>
                {poles.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          )}
          <button onClick={charger} className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-gray-700 dark:text-gray-200">
            Charger
          </button>
          {rows.length > 0 && (
            <button onClick={toutPresent} className="px-4 py-2 text-sm bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-100 rounded-lg">
              Tout présent
            </button>
          )}
          <div className="ml-auto text-xs text-gray-500">{jourLabel(date)} · {rows.length} cours · {nbNotes} noté(s)</div>
        </div>
      </div>

      {msg && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
          msg.t === 'ok' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
          : msg.t === 'err' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
          : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'}`}>
          {msg.t === 'ok' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}<span>{msg.m}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-2 text-gray-500 py-16"><Loader2 className="animate-spin" size={18} /> Chargement…</div>
      ) : rows.length > 0 && (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Créneau</th>
                  <th className="text-left px-3 py-2">Classe</th>
                  <th className="text-left px-3 py-2">Matière</th>
                  <th className="text-left px-3 py-2">Formateur</th>
                  <th className="text-center px-3 py-2">Présence</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.edt_id + '|' + i} className="border-t border-gray-100 dark:border-gray-700/50 align-top">
                    <td className="px-3 py-2 whitespace-nowrap text-gray-600 dark:text-gray-300">{r.creneau}</td>
                    <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-100">{r.classe}</td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-200">
                      {r.libelle_matiere}
                      {r.pole_nom && <span className="block text-xs text-gray-400">{r.pole_nom}</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-200">
                      {r.formateur_nom || <span className="text-gray-400 italic">non renseigné</span>}
                      {r.statut === 'remplace' && (
                        <input value={r.remplacant_nom} onChange={(e) => setChamp(i, 'remplacant_nom', e.target.value)}
                          placeholder="Remplaçant…"
                          className="mt-1 block w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700" />
                      )}
                      {(r.statut === 'absent' || r.statut === 'excuse') && (
                        <input value={r.motif} onChange={(e) => setChamp(i, 'motif', e.target.value)}
                          placeholder="Motif (optionnel)…"
                          className="mt-1 block w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700" />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-center gap-1">
                        {STATUTS.map((s) => (
                          <button key={s.v} onClick={() => setStatut(i, s.v)} title={s.label}
                            className={`px-2.5 py-1 rounded text-xs font-semibold border ${
                              r.statut === s.v ? `${s.bg} ${s.fg} border-current` : 'border-gray-200 dark:border-gray-600 text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                            {s.court}
                          </button>
                        ))}
                      </div>
                      {r.saisi && <div className="text-[10px] text-gray-400 text-center mt-1">déjà noté{r.saisi_par_nom ? ` · ${r.saisi_par_nom}` : ''}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4">
            <div className="flex flex-wrap gap-3 text-xs text-gray-500">
              {STATUTS.map((s) => (
                <span key={s.v} className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full" style={{ background: s.dot }} /> {s.court} = {s.label}
                </span>
              ))}
            </div>
            <button onClick={enregistrer} disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Enregistrer
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ───────────────────────── Dashboard (DAAC) ─────────────────────────
interface FormRow {
  nom: string; pole_nom: string; present: number; absent: number; remplace: number;
  excuse: number; total: number; taux_presence: number | null;
  jours: Record<string, { present: number; absent: number; remplace: number; excuse: number; total: number }>;
}
const dominant = (j: { present: number; absent: number; remplace: number; excuse: number }) => {
  if (!j) return '';
  if (j.absent > 0) return 'absent';
  if (j.excuse > 0) return 'excuse';
  if (j.remplace > 0) return 'remplace';
  if (j.present > 0) return 'present';
  return '';
};

const Dashboard: React.FC = () => {
  const [from, setFrom] = useState(addDays(todayISO(), -6));
  const [to, setTo] = useState(todayISO());
  const [formateur, setFormateur] = useState('');
  const [dates, setDates] = useState<string[]>([]);
  const [rows, setRows] = useState<FormRow[]>([]);
  const [totaux, setTotaux] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [resume, setResume] = useState<any>(null);

  // Résumé du jour + on marque les saisies comme « vues » (efface le badge).
  useEffect(() => {
    apiClient.get('/vie-scolaire/presence-formateurs/resume/')
      .then((r) => setResume(r.data))
      .catch(() => {});
    try { localStorage.setItem('presence_formateurs_last_seen', new Date().toISOString()); } catch {}
    window.dispatchEvent(new Event('presence-vu'));
  }, []);

  const charger = useCallback(async () => {
    setLoading(true); setMsg(null);
    try {
      const r = await apiClient.get('/vie-scolaire/presence-formateurs/dashboard/', {
        params: { from, to, ...(formateur ? { formateur } : {}) },
      });
      setDates(r.data.dates || []);
      setRows(r.data.formateurs || []);
      setTotaux(r.data.totaux || null);
      if ((r.data.formateurs || []).length === 0) setMsg('Aucune présence enregistrée sur cette période.');
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || 'Chargement impossible.');
    } finally { setLoading(false); }
  }, [from, to, formateur]);

  useEffect(() => { charger(); /* eslint-disable-next-line */ }, []);

  const preset = (n: number) => { setFrom(addDays(todayISO(), -(n - 1))); setTo(todayISO()); };

  const cards = useMemo(() => {
    if (!totaux) return [];
    const fait = totaux.present + totaux.remplace;
    const taux = totaux.total ? Math.round((1000 * fait) / totaux.total) / 10 : null;
    return [
      { l: 'Cours notés', v: totaux.total, c: 'text-gray-800 dark:text-white' },
      { l: 'Présences', v: totaux.present + totaux.remplace, c: 'text-green-600' },
      { l: 'Absences', v: totaux.absent, c: 'text-red-600' },
      { l: 'Taux de présence', v: taux != null ? `${taux}%` : '—', c: 'text-blue-600' },
    ];
  }, [totaux]);

  return (
    <div>
      {resume && (
        <div className="mb-4 p-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-sm text-blue-900 dark:text-blue-200 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="font-semibold">Aujourd'hui</span>
          <span><b>{resume.saisi_aujourdhui}</b> cours notés{resume.cours_prevus ? ` sur ${resume.cours_prevus} prévus` : ''}</span>
          <span className="text-green-700 dark:text-green-300">{resume.present} présents</span>
          <span className="text-red-700 dark:text-red-300">{resume.absent} absents</span>
          {resume.non_saisis > 0 && <span className="text-amber-700 dark:text-amber-300">{resume.non_saisis} non notés</span>}
        </div>
      )}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Du</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Au</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Formateur</label>
            <input value={formateur} onChange={(e) => setFormateur(e.target.value)} placeholder="Rechercher…"
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
          </div>
          <button onClick={charger} className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Afficher</button>
          <div className="flex gap-1 ml-auto">
            {[7, 14, 30].map((n) => (
              <button key={n} onClick={() => preset(n)} className="px-2.5 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
                {n} j
              </button>
            ))}
          </div>
        </div>
      </div>

      {cards.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {cards.map((c) => (
            <div key={c.l} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-xs text-gray-500">{c.l}</div>
              <div className={`text-2xl font-bold ${c.c}`}>{c.v}</div>
            </div>
          ))}
        </div>
      )}

      {msg && (
        <div className="mb-4 p-3 rounded-lg flex items-center gap-2 text-sm bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
          <AlertCircle size={18} /><span>{msg}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-2 text-gray-500 py-16"><Loader2 className="animate-spin" size={18} /> Chargement…</div>
      ) : rows.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-x-auto">
          <table className="text-sm border-collapse">
            <thead>
              <tr className="text-gray-500 text-xs">
                <th className="text-left px-3 py-2 sticky left-0 bg-white dark:bg-gray-800 z-10 min-w-[180px]">Formateur</th>
                {dates.map((d) => (
                  <th key={d} className="px-1 py-2 font-medium whitespace-nowrap" title={d}>{jourLabel(d)}</th>
                ))}
                <th className="px-3 py-2">Taux</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => (
                <tr key={f.nom} className="border-t border-gray-100 dark:border-gray-700/50">
                  <td className="px-3 py-1.5 sticky left-0 bg-white dark:bg-gray-800 z-10">
                    <div className="font-medium text-gray-800 dark:text-gray-100">{f.nom}</div>
                    {f.pole_nom && <div className="text-[10px] text-gray-400">{f.pole_nom}</div>}
                  </td>
                  {dates.map((d) => {
                    const j = f.jours[d];
                    const dom = dominant(j as any);
                    const st = dom ? STAT_MAP[dom] : null;
                    return (
                      <td key={d} className="px-1 py-1.5 text-center">
                        {st ? (
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded text-xs font-semibold ${st.bg} ${st.fg}`}
                            title={`${f.nom} · ${jourLabel(d)} : ${st.label}${j.total > 1 ? ` (${j.total} cours)` : ''}`}>
                            {j.total > 1 ? j.total : st.court}
                          </span>
                        ) : <span className="text-gray-200 dark:text-gray-600">·</span>}
                      </td>
                    );
                  })}
                  <td className="px-3 py-1.5 text-center font-semibold text-gray-700 dark:text-gray-200">
                    {f.taux_presence != null ? `${f.taux_presence}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 && (
        <div className="flex flex-wrap gap-3 text-xs text-gray-500 mt-3">
          {STATUTS.map((s) => (
            <span key={s.v} className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded" style={{ background: s.dot }} /> {s.label}
            </span>
          ))}
          <span className="text-gray-400">Un chiffre dans la case = nombre de cours ce jour-là.</span>
        </div>
      )}
    </div>
  );
};

export default PresenceFormateurs;
